import pluralize from 'pluralize';
import { snapshot } from 'valtio';
import { Nullable } from 'common-types';
import { openSnackbar } from '../../snackbar/actions/snackbar-actions';
import {
  PolicySettingCategory,
  PolicyUpdateOption,
} from '../policy-settings-types';
import policySettingsService from '../services';
import PolicySettingsStore from '../store/policy-settings-store';
import {
  setPolicySettings,
  setPolicySetting,
  setPolicySettingsLoading,
} from '../store/store-actions';

let INIT_POLICY_SETTINGS: PolicySettingCategory[] | null = null;

export const fetchPolicySettings = async (appIds: string[]) => {
  setPolicySettingsLoading(true);
  const policySettings = await policySettingsService.getPolicySettings.execute({
    appIds,
  });
  INIT_POLICY_SETTINGS = policySettings;
  policySettings && setPolicySettings(policySettings);
  setPolicySettingsLoading(false);
};

const getPolicySetting = (
  data: Nullable<PolicySettingCategory[]>,
  policyId: string,
  categoryId: string,
) => {
  const category = data?.find(category => category.categoryId === categoryId);
  return category?.settings?.find(policy => policy.policyId === policyId);
};

export const restorePolicySettings = (policyId: string, categoryId: string) => {
  const defaultValue = getPolicySetting(
    INIT_POLICY_SETTINGS,
    policyId,
    categoryId,
  );

  defaultValue && setPolicySetting(policyId, categoryId, defaultValue);
};

export const selectOptionForPolicy = (
  optionId: string,
  policyId: string,
  categoryId: string,
  type: 'new' | 'old',
) => {
  const { policySettings } = snapshot(PolicySettingsStore);
  const policy = getPolicySetting(policySettings, policyId, categoryId);
  if (policy) {
    const appsCount = policy.options[type].reduce(
      (acc, option) => acc + option.appsCount,
      0,
    );
    const newPolicy = {
      ...policy,
      hasChanged: true,
      options: {
        ...policy.options,
        [type]: policy.options[type].map(option =>
          option.optionId === optionId
            ? {
                ...option,
                selected: true,
                appsCount,
              }
            : { ...option, selected: false, appsCount: 0 },
        ),
      },
    };
    setPolicySetting(policyId, categoryId, newPolicy);
  }
};

export const submitPolicySettings = async (appIds: string[]) => {
  const { policySettings } = snapshot(PolicySettingsStore);

  const policySettingsInput = policySettings?.reduce((acc, category) => {
    category.settings.forEach(policy => {
      if (policy.hasChanged) {
        const newOptionId =
          policy.options.new.find(option => option.selected)?.optionId || '';
        const oldOptionId =
          policy.options.old.find(option => option.selected)?.optionId || '';
        acc.push({
          policyId: policy.policyId,
          ...(newOptionId && { newOptionId }),
          ...(oldOptionId && { oldOptionId }),
        });
      }
    });
    return acc;
  }, [] as PolicyUpdateOption[]);
  const success = await policySettingsService.setPolicySettings.execute({
    appIds,
    policySettings: policySettingsInput,
  });
  openSnackbar(
    success
      ? `Pipeline settings for selected ${pluralize(
          'app',
          appIds.length,
        )}  were saved.`
      : `Failed to save pipeline settings for selected ${pluralize(
          'app',
          appIds.length,
        )}`,
    { variant: success ? 'success' : 'error' },
  );
};

export const checkPoliciesChanges = () => {
  const { policySettings } = snapshot(PolicySettingsStore);
  return policySettings.reduce((acc, category) => {
    category.settings.forEach(policy => {
      acc = acc || !!policy.hasChanged;
    });
    return acc;
  }, false);
};
